import Phaser from 'phaser';
import { GAME_CONFIG, CHARACTERS } from '../config/game';

// Version format: YYYY.MM.DD.HHMM (updated each deploy)
const GAME_VERSION = '2025.12.08.1825';

export class TitleScene extends Phaser.Scene {
  private titleText!: Phaser.GameObjects.Text;
  private subtitleText!: Phaser.GameObjects.Text;
  private familySprites: Phaser.GameObjects.Image[] = [];

  constructor() {
    super('TitleScene');
  }

  create() {
    const { width, height } = GAME_CONFIG;

    // Background gradient effect
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x1a1a2e, 0x1a1a2e, 0x16213e, 0x16213e, 1);
    bg.fillRect(0, 0, width, height);

    // Decorative stars
    for (let i = 0; i < 50; i++) {
      const star = this.add.graphics();
      const x = Phaser.Math.Between(0, width);
      const y = Phaser.Math.Between(0, height * 0.6);
      const size = Phaser.Math.FloatBetween(1, 3);
      star.fillStyle(0xffffff, Phaser.Math.FloatBetween(0.3, 1));
      star.fillCircle(x, y, size);
      
      // Twinkle animation
      this.tweens.add({
        targets: star,
        alpha: { from: 0.3, to: 1 },
        duration: Phaser.Math.Between(1000, 3000),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // Main title with animation
    this.titleText = this.add.text(width / 2, height * 0.25, 'THE SILVER SAGA', {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: '42px',
      color: '#ffd700',
      stroke: '#4a3800',
      strokeThickness: 6,
      shadow: {
        offsetX: 2,
        offsetY: 2,
        color: '#000000',
        blur: 4,
        fill: true,
      },
    });
    this.titleText.setOrigin(0.5, 0.5);

    // Title animation
    this.tweens.add({
      targets: this.titleText,
      scaleX: { from: 0.8, to: 1 },
      scaleY: { from: 0.8, to: 1 },
      duration: 800,
      ease: 'Back.easeOut',
    });

    // Subtitle
    this.subtitleText = this.add.text(width / 2, height * 0.33, 'A Family Platformer', {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      color: '#e0e0e0',
    });
    this.subtitleText.setOrigin(0.5, 0.5);
    this.subtitleText.setAlpha(0);

    this.tweens.add({
      targets: this.subtitleText,
      alpha: 1,
      delay: 500,
      duration: 800,
    });

    // Family character lineup
    this.createFamilyLineup(width, height);

    // Start button - with larger touch target for mobile
    const startButton = this.add.image(width / 2, height * 0.75, 'button');
    // Use a larger hit area for better touch support (button is 180x50, hit area is 220x70)
    startButton.setInteractive({
      useHandCursor: true,
      hitArea: new Phaser.Geom.Rectangle(-20, -10, 220, 70),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
    });
    
    const startText = this.add.text(width / 2, height * 0.75, 'START', {
      fontFamily: 'Georgia, serif',
      fontSize: '24px',
      color: '#1a1a2e',
      fontStyle: 'bold',
    });
    startText.setOrigin(0.5, 0.5);

    // Button hover effect
    startButton.on('pointerover', () => {
      this.tweens.add({
        targets: [startButton, startText],
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 100,
      });
    });

    startButton.on('pointerout', () => {
      this.tweens.add({
        targets: [startButton, startText],
        scaleX: 1,
        scaleY: 1,
        duration: 100,
      });
    });

    startButton.on('pointerdown', () => {
      this.cameras.main.fadeOut(500, 0, 0, 0);
      this.time.delayedCall(500, () => {
        this.scene.start('WorldMapScene');
      });
    });

    // Game premise
    const premise = this.add.text(width / 2, height * 0.64, 
      'Help the Silver family survive\nthe daily chaos of parenting!', {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#a0a0a0',
      align: 'center',
    });
    premise.setOrigin(0.5, 0.5);
    premise.setAlpha(0);

    this.tweens.add({
      targets: premise,
      alpha: 1,
      delay: 800,
      duration: 600,
    });

    // Controls hint
    const controlsHint = this.add.text(width / 2, height * 0.85, 
      '🎮 Arrow Keys / WASD to move • SPACE to jump • SHIFT to dash', {
      fontFamily: 'Georgia, serif',
      fontSize: '11px',
      color: '#888888',
    });
    controlsHint.setOrigin(0.5, 0.5);

    // Credits
    const credits = this.add.text(width / 2, height - 30, 'The Silver Family © 2024', {
      fontFamily: 'Georgia, serif',
      fontSize: '12px',
      color: '#666666',
    });
    credits.setOrigin(0.5, 0.5);

    // Version number (bottom right)
    const version = this.add.text(width - 10, height - 10, `v${GAME_VERSION}`, {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#444444',
    });
    version.setOrigin(1, 1);

    // Fade in
    this.cameras.main.fadeIn(500, 0, 0, 0);
  }

  private createFamilyLineup(width: number, height: number) {
    // Character sprite keys in order: parents first, then kids by age
    const characterOrder = [
      { key: 'david', name: 'David' },
      { key: 'enny', name: 'Enny' },
      { key: 'adam', name: 'Adam' },
      { key: 'eli', name: 'Eli' },
      { key: 'emily', name: 'Emily' },
      { key: 'daniel', name: 'Daniel' },
    ];
    
    const startX = width / 2 - (characterOrder.length - 1) * 35;
    const y = height * 0.52;

    characterOrder.forEach((char, index) => {
      const x = startX + index * 70;
      
      // Try to use the actual sprite, fall back to a colored circle if not available
      const spriteKey = `${char.key}_sprite`;
      
      if (this.textures.exists(spriteKey)) {
        // Use actual sprite
        const sprite = this.add.image(x, y, spriteKey);
        sprite.setScale(1.8); // Scale up for visibility
        sprite.setOrigin(0.5, 0.5);
        
        // Bouncing animation with delay
        this.tweens.add({
          targets: sprite,
          y: y - 10,
          duration: 600,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay: index * 100,
        });
        
        this.familySprites.push(sprite);
      } else {
        // Fallback: draw a colored placeholder
        const charData = Object.values(CHARACTERS).find(c => c.name === char.name);
        const color = charData?.color || 0x888888;
        
        const placeholder = this.add.graphics();
        placeholder.fillStyle(color, 1);
        placeholder.fillCircle(0, 0, 24);
        placeholder.fillStyle(0xffd9b3, 1);
        placeholder.fillCircle(0, -8, 10); // Head
        placeholder.setPosition(x, y);
        
        this.tweens.add({
          targets: placeholder,
          y: y - 10,
          duration: 600,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
          delay: index * 100,
        });
      }

      // Name label
      const nameLabel = this.add.text(x, y + 50, char.name, {
        fontFamily: 'Georgia, serif',
        fontSize: '11px',
        color: '#ffffff',
      });
      nameLabel.setOrigin(0.5, 0.5);
    });
  }
}

